import {Box, Stack, Typography, Alert} from '@mui/material'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import Image from '../customTable/Image'
import MainBtn from '../customTable/MainBtn'
import PopUp from '../customTable/PopUp'
import Temps from '../../Api/Temps'
import {useSelector} from 'react-redux'
import {RootState} from '../../store'
import PaymentGatewaySelector from '../wallet/components/PaymentGatewaySelector'
import OTPModal from '../wallet/components/OTPModal'
import {
  handlePaymentInitiationResponse,
  parsePaymentAmount,
  formatPaymentDisplay,
  resolveLocalizedString,
  isBlomUnifiedCheckout,
  runBlomUnifiedCheckout,
  toIsoCurrencyCode,
} from '../../utils/paymentHelpers'

const PRIMARY = '#2C3E6B'
const SURFACE = '#F8F8F8'
const MUTED = '#65656A'

type FlowStep = 'home' | 'otp' | 'gateway'

/**
 * Single modal for template change:
 * - Country change_template_price <= 0 → free change (direct)
 * - Price > 0 → OTP → PaymentGatewaySelector → pay
 * Template.is_free does NOT skip country fee (admin setting is source of truth).
 */
export default function TemplateCard({current, i, data, setCurrent}: any) {
  const store = useSelector((state: RootState) => state.vender)
  const {t} = useTranslation()
  const {lang, defLang} = store

  const [open, setOpen] = useState(false)
  const [flowStep, setFlowStep] = useState<FlowStep>('home')
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [signature, setSignature] = useState('')
  const [otpAmountLabel, setOtpAmountLabel] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const countryId = Number(localStorage.getItem('country_id') || 0)
  const currency = (localStorage.getItem('currency') || 'USD').trim()
  // Country admin setting — not template.is_free
  const templatePrice = parsePaymentAmount(store.sizeImages?.change_template_price)
  const isFreeTemplate = templatePrice <= 0
  const priceLabel = isFreeTemplate
    ? String(t('free') || 'Free')
    : formatPaymentDisplay(templatePrice, currency)
  const imageSrc = store.isRtl === 1 ? data.rtl_image : data.ltr_image || data.rtl_image
  const templateName = resolveLocalizedString(data?.name, lang, defLang) || String(data?.name || '')

  const resetFlow = () => {
    setFlowStep('home')
    setSignature('')
    setOtpAmountLabel('')
    setErrorMsg('')
  }

  const closeModal = () => {
    if (isLoading) {
      return
    }
    setOpen(false)
    resetFlow()
  }

  const applyFreeTemplate = () => {
    setErrorMsg('')
    Temps.change(
      {template_id: data.id},
      () => {
        setCurrent(data.id)
        closeModal()
      },
      setIsLoading
    )
  }

  const startPaidOtpFlow = () => {
    setErrorMsg('')
    Temps.change(
      {template_id: data.id},
      (res: any) => {
        console.log('Template change-template response:', res)
        const nextSignature = res?.signature ?? res?.data?.signature
        if (!nextSignature) {
          setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start verification'))
          return
        }
        const amount = res?.amount ?? res?.data?.amount ?? templatePrice
        const curr = res?.currency ?? res?.data?.currency ?? currency
        const label = formatPaymentDisplay(amount, curr)
        console.log('OTP modal props:', {amount, currency: curr, label, planName: templateName})
        setSignature(String(nextSignature))
        setOtpAmountLabel(label)
        setFlowStep('otp')
      },
      setIsLoading
    )
  }

  const handleOtpSubmit = (code: string) => {
    setErrorMsg('')
    Temps.confirmOtp(
      {template_id: data.id, signature, code},
      () => setFlowStep('gateway'),
      setIsLoading
    )
  }

  const handleGatewaySelected = (factoryName: string) => {
    setErrorMsg('')
    const currency_code = toIsoCurrencyCode(currency)

    if (isBlomUnifiedCheckout(factoryName)) {
      runBlomUnifiedCheckout(
        {
          purpose: 'template',
          template_id: data.id,
          currency_code,
        },
        setIsLoading,
        (message) => setErrorMsg(message)
      )
      return
    }

    Temps.payByGateway(
      factoryName,
      {template_id: data.id, currency_code},
      (res: any) => {
        if (!handlePaymentInitiationResponse(res)) {
          setErrorMsg(String(t('paymentGatewaysLoadError') || 'Unable to start payment'))
        }
      },
      setIsLoading
    )
  }

  return (
    <>
      <Stack
        p='16px'
        gap='18px'
        width={{lg: '195px', sm: '40%', xs: '80%', md: 'calc(50% - 12px)'}}
        borderRadius='8px'
        bgcolor={SURFACE}
      >
        <Stack height='20px'>
          <Typography sx={{fontSize: '14px', fontWeight: 500, color: PRIMARY}} noWrap>
            {templateName || '—'}
          </Typography>
        </Stack>
        <Box sx={{width: '100%', height: '148px'}}>
          <Image sx={{height: '100%', width: '100%', borderRadius: '8px'}} src={imageSrc} />
        </Box>
        {i === current || data.is_choice ? (
          <Stack justifyContent='center'>
            <MainBtn handelSubmit={() => {}} text='currentTemplate' without />
          </Stack>
        ) : (
          <Stack gap='16px' justifyContent='center' direction='row'>
            {store.permissions && store.permissions.includes('template_update') && (
              <MainBtn
                minWidth='0px'
                width='73px'
                handelSubmit={() => {
                  resetFlow()
                  setOpen(true)
                }}
                text='select'
              />
            )}
            <MainBtn
              minWidth='0px'
              width='73px'
              handelSubmit={() =>
                window.open(store.isRtl === 1 ? data.rtl_link : data.ltr_link, '_blank')
              }
              text='preview'
              without
            />
          </Stack>
        )}

        <PopUp width='sm' open={open} setOpen={setOpen} isLoading={isLoading}>
          <Stack
            p={{xs: '16px', sm: '24px', lg: '32px'}}
            gap={{xs: '12px', sm: '16px'}}
            bgcolor='#fff'
            sx={{maxWidth: '100%', overflowX: 'hidden'}}
          >
            {/* Home step only — avoid duplicating name/price inside OTP / gateway */}
            {flowStep === 'home' && (
              <Stack alignItems='center' gap='12px'>
                {imageSrc ? (
                  <Box
                    component='img'
                    src={imageSrc}
                    alt=''
                    sx={{width: 72, height: 72, borderRadius: '8px', objectFit: 'cover'}}
                  />
                ) : null}
                <Typography sx={{fontSize: {xs: '16px', sm: '18px'}, fontWeight: 600, color: PRIMARY, textAlign: 'center'}}>
                  {templateName || t('purchaseTemplate')}
                </Typography>
                <Typography sx={{fontSize: {xs: '13px', sm: '14px'}, color: MUTED, textAlign: 'center'}}>
                  {t('yourAreGoingChangeYourStoreTemplate')}
                </Typography>
                <Box
                  sx={{
                    bgcolor: SURFACE,
                    borderRadius: '8px',
                    px: '16px',
                    py: '12px',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{fontSize: {xs: '18px', sm: '20px'}, fontWeight: 700, color: PRIMARY}}>
                    {priceLabel}
                  </Typography>
                </Box>
              </Stack>
            )}

            {errorMsg ? <Alert severity='error'>{errorMsg}</Alert> : null}

            {flowStep === 'home' && isFreeTemplate && (
              <Stack direction='row' gap='12px' justifyContent='center' flexWrap='wrap'>
                <MainBtn
                  isLoading={isLoading}
                  width='fit-content'
                  handelSubmit={applyFreeTemplate}
                  text='changeTemplate'
                  bgcolor={PRIMARY}
                />
                <MainBtn width='fit-content' handelSubmit={closeModal} text='cancel' without />
              </Stack>
            )}

            {flowStep === 'home' && !isFreeTemplate && (
              <Stack direction='row' gap='12px' justifyContent='center' flexWrap='wrap'>
                <MainBtn
                  isLoading={isLoading}
                  width='fit-content'
                  handelSubmit={startPaidOtpFlow}
                  text='continueToPayment'
                  bgcolor={PRIMARY}
                />
                <MainBtn width='fit-content' handelSubmit={closeModal} text='cancel' without />
              </Stack>
            )}

            {flowStep === 'otp' && (
              <OTPModal
                signature={signature}
                planName={templateName}
                amountLabel={otpAmountLabel || formatPaymentDisplay(templatePrice, currency)}
                amount={templatePrice}
                currency={currency}
                titleKey='verifyYourCode'
                isLoading={isLoading}
                isResending={isResending}
                onSubmit={handleOtpSubmit}
                onCancel={closeModal}
                onResend={() => Temps.resendOtp({signature}, () => {}, setIsResending)}
              />
            )}

            {flowStep === 'gateway' && (
              <PaymentGatewaySelector
                open={open && flowStep === 'gateway'}
                onClose={closeModal}
                amount={templatePrice}
                currency={currency}
                purpose='template'
                countryId={countryId}
                referenceId={Number(data.id) || 0}
                isPaying={isLoading}
                confirmTextKey='purchaseTemplate'
                onGatewaySelected={handleGatewaySelected}
                summary={{
                  titleKey: 'purchaseTemplate',
                  planName: templateName,
                  description: String(t('templatePurchaseDescription') || ''),
                  imageSrc,
                }}
              />
            )}
          </Stack>
        </PopUp>
      </Stack>
    </>
  )
}
